import React, { useState, useEffect, useMemo, useRef } from 'react';
import Header from '../../layouts/Header';
import { Button, Dropdown, Form, Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axios';
import makeAnimated from 'react-select/animated';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import {
    Search, Filter, ArrowClockwise, Plus, Megaphone, Eye, EyeSlash, ExclamationCircle,
    ThreeDotsVertical, ChevronLeft, ChevronRight, ChevronExpand, Pencil, Trash,
    Image as ImageIcon, CloudArrowUp, InfoCircle,
} from 'react-bootstrap-icons';

/* ------------------------------------------------------------------ */
/* Constants & helpers                                                  */
/* ------------------------------------------------------------------ */
const ADMIN_EMAIL = 'contact@xpansecafe.com';
const ALLOWED_EXT = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'mp4', 'mov', 'avi', 'mkv', 'webm'];
const VIDEO_RE = /\.(mp4|webm|ogg|mov)$/i;
const INVALID_FORMAT_MSG = 'Invalid file format. Upload only images (png, jpg, jpeg, webp, gif) or videos (mp4, mov, avi, mkv, webm).';
const toastBase = { position: 'top-right', autoClose: 3000, theme: 'colored' };

const notifyError = (msg) => toast.error(msg || 'Something went wrong. Please try again.', toastBase);
const notifySuccess = (msg) => toast.success(msg || 'Done', toastBase);

const asText = (v) => (typeof v === 'string' && v ? v : undefined);

const getErrorMessage = (error) => {
    const res = error?.response;
    if (!res) return 'Network error. Please check your connection.';
    const d = res.data;
    if (res.status === 406) return asText(d?.error?.message) || asText(d?.error);
    if (res.status === 412) return asText(d?.data?.message);
    if (res.status === 404) return asText(d?.data?.message) || asText(d?.error);
    return asText(d?.error?.message) || asText(d?.error) || asText(d?.message) || asText(d?.data?.message);
};

const formatDate = (d, withTime = true) =>
    d
        ? new Date(d).toLocaleString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
            ...(withTime ? { hour: '2-digit', minute: '2-digit', hour12: true } : {}),
        })
        : '--------';

const formatSize = (bytes = 0) =>
    bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const getScreenLabel = (row) =>
    row?.redirectTo === 'PRODUCT' ? 'PRODUCT_SCREEN'
        : row?.redirectTo === 'MENU' && row?.isSplashScreenAd === false ? 'MENU_SCREEN'
            : row?.redirectTo === 'SUBSCRIPTION' ? 'SUBSCRIPTION_SCREEN'
                : row?.isSplashScreenAd ? 'SPLASH_SCREEN_AD' : 'PROFILE_SCREEN';

const screenClass = (label) => ({
    PRODUCT_SCREEN: 'green', MENU_SCREEN: 'indigo', SUBSCRIPTION_SCREEN: 'amber',
    SPLASH_SCREEN_AD: 'pink', PROFILE_SCREEN: 'gray',
}[label] || 'gray');

const excludedText = (row) =>
    row?.excludedOutlets?.length > 0
        ? row.excludedOutlets.map((o) => o.name.toLowerCase().trim()).slice(0, 2).join(', ')
        + (row.excludedOutlets.length > 2 ? ` +${row.excludedOutlets.length - 2} more` : '')
        : 'No outlets excluded';

const REDIRECT_OPTIONS = [
    { value: 'MENU', label: 'Menu' },
    { value: 'PRODUCT', label: 'Product' },
    { value: 'SUBSCRIPTION', label: 'Subscription' },
    { value: 'PROFILE', label: 'Profile' },
];

const selectStyles = {
    control: (p, s) => ({
        ...p, minHeight: 32, fontSize: '0.78rem', borderRadius: 6,
        borderColor: s.isFocused ? '#8b8cf8' : '#dfe3ee',
        boxShadow: s.isFocused ? '0 0 0 0.15rem rgba(99,102,241,.18)' : 'none',
        '&:hover': { borderColor: s.isFocused ? '#8b8cf8' : '#dfe3ee' },
    }),
    valueContainer: (p) => ({ ...p, padding: '0 8px' }),
    indicatorsContainer: (p) => ({ ...p, height: 30 }),
    dropdownIndicator: (p) => ({ ...p, padding: 4 }),
    clearIndicator: (p) => ({ ...p, padding: 4 }),
    placeholder: (p) => ({ ...p, color: '#9aa1b5', fontSize: '0.78rem' }),
    menu: (p) => ({ ...p, fontSize: '0.78rem' }),
    menuPortal: (p) => ({ ...p, zIndex: 2000 }),
    option: (p) => ({ ...p, fontSize: '0.78rem', padding: '5px 10px' }),
    multiValue: (p) => ({ ...p, fontSize: '0.72rem', background: '#eef0ff' }),
    multiValueLabel: (p) => ({ ...p, fontSize: '0.72rem', padding: '1px 5px', color: '#4338ca' }),
};

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */
const css = `
.pa-wrap{display:flex;flex-direction:column;gap:12px}
.pa-card{background:#fff;border:1px solid #edf0f7;border-radius:10px;box-shadow:0 1px 2px rgba(20,24,60,.04)}

/* hero */
.pa-hero{position:relative;overflow:hidden;display:flex;align-items:center;justify-content:space-between;padding:16px 22px;border-radius:10px;color:#fff;background:linear-gradient(110deg,#1b1946 0%,#272665 55%,#3a3a8c 100%)}
.pa-hero-left{display:flex;align-items:center;gap:14px;position:relative;z-index:1}
.pa-hero-icon{width:44px;height:44px;border-radius:12px;display:flex;align-items:center;justify-content:center;font-size:1.2rem;background:linear-gradient(135deg,#6366f1,#8b8cf8);box-shadow:0 6px 16px rgba(99,102,241,.45)}
.pa-hero h1{font-size:1.05rem;font-weight:700;margin:0 0 2px;color:#fff}
.pa-hero p{margin:0;font-size:.78rem;color:#c9cbf0}
.pa-hero-art{position:relative;width:150px;height:60px;margin-right:30px}
.pa-hero-art::before{content:"";position:absolute;left:10px;top:-30px;width:130px;height:130px;border-radius:50%;background:radial-gradient(circle,rgba(139,140,248,.45),transparent 70%)}
.pa-hero-tile{position:absolute;left:48px;top:-4px;width:58px;height:58px;border-radius:14px;display:flex;align-items:center;justify-content:center;font-size:1.6rem;color:#fff;background:linear-gradient(135deg,#a5a6fb,#7c7ef0);box-shadow:0 8px 20px rgba(0,0,0,.25)}
.pa-hero-new{position:absolute;right:-10px;top:-12px;background:#f43f5e;color:#fff;font-size:.58rem;font-weight:700;padding:1px 6px;border-radius:10px}

/* toolbar */
.pa-toolbar{display:flex;flex-wrap:wrap;align-items:flex-end;gap:12px;padding:12px 14px}
.pa-field{display:flex;flex-direction:column;gap:3px}
.pa-field label{font-size:.72rem;font-weight:600;color:#3b4160;margin:0}
.pa-search{display:flex}
.pa-search input{border-radius:6px 0 0 6px!important;min-width:250px}
.pa-search button{border-radius:0 6px 6px 0!important}
.pa-input{font-size:.78rem!important;border-color:#dfe3ee!important;border-radius:6px!important;height:32px;padding-top:0!important;padding-bottom:0!important}
.pa-input:focus{border-color:#8b8cf8!important;box-shadow:0 0 0 .15rem rgba(99,102,241,.18)!important}
.pa-btn{font-size:.78rem!important;height:32px;padding:0 12px!important;border-radius:6px!important;display:inline-flex!important;align-items:center;gap:5px;font-weight:600}
.pa-btn-primary{background:#4f46e5!important;border-color:#4f46e5!important;color:#fff!important}
.pa-btn-primary:hover{background:#4338ca!important}
.pa-btn-ghost{background:#fff!important;border:1px solid #dfe3ee!important;color:#3b4160!important}
.pa-btn-ghost:hover{background:#f6f7fc!important}
.pa-spacer{flex:1}

/* stats */
.pa-stats{display:grid;grid-template-columns:repeat(4,1fr);gap:12px}
@media(max-width:991px){.pa-stats{grid-template-columns:repeat(2,1fr)}}
.pa-stat{display:flex;align-items:center;gap:12px;padding:12px 14px;border-radius:10px;border:1px solid #edf0f7}
.pa-stat-icon{width:38px;height:38px;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:1.05rem}
.pa-stat b{display:block;font-size:1.05rem;color:#1f2340;line-height:1.1}
.pa-stat span{font-size:.75rem;color:#6b7190}
.pa-stat.indigo{background:linear-gradient(135deg,#fff 45%,#eef0ff)}.pa-stat.indigo .pa-stat-icon{background:#e3e6ff;color:#4f46e5}
.pa-stat.green{background:linear-gradient(135deg,#fff 45%,#e9f9ef)}.pa-stat.green .pa-stat-icon{background:#d5f5e1;color:#16a34a}
.pa-stat.amber{background:linear-gradient(135deg,#fff 45%,#fff6e0)}.pa-stat.amber .pa-stat-icon{background:#fdecc0;color:#d97706}
.pa-stat.rose{background:linear-gradient(135deg,#fff 45%,#ffe9ec)}.pa-stat.rose .pa-stat-icon{background:#ffd9de;color:#e11d48}

/* table */
.pa-table-wrap{overflow-x:auto}
.pa-table{width:100%;border-collapse:separate;border-spacing:0;font-size:.78rem}
.pa-table th{padding:11px 14px;font-weight:600;color:#2b3050;background:#fafbff;border-bottom:1px solid #edf0f7;white-space:nowrap;font-size:.75rem}
.pa-table th.sortable{cursor:pointer;user-select:none}
.pa-table th .pa-sort{margin-left:5px;color:#b3b8cc}
.pa-table th.active .pa-sort{color:#4f46e5}
.pa-table td{padding:10px 14px;border-bottom:1px solid #f1f3f9;color:#2b3050;vertical-align:middle}
.pa-table tbody tr:hover{background:#fafbff}
.pa-table tbody tr:last-child td{border-bottom:0}
.pa-sr{display:inline-flex;align-items:center;justify-content:center;min-width:28px;height:24px;padding:0 6px;border-radius:6px;background:#f1f3f9;font-weight:600;color:#3b4160;font-size:.74rem}
.pa-title-cell{display:flex;align-items:center;gap:10px}
.pa-avatar{width:40px;height:40px;border-radius:50%;object-fit:cover;border:2px solid #e3e6ff;cursor:pointer;flex-shrink:0;background:#f1f3f9}
.pa-title{font-weight:600;color:#1f2340;font-size:.8rem;line-height:1.2}
.pa-date{font-size:.68rem;color:#8a90a6}
.pa-pill{display:inline-block;padding:3px 9px;border-radius:5px;font-size:.66rem;font-weight:700;letter-spacing:.01em}
.pa-pill.green{background:#dcfce7;color:#15803d}.pa-pill.indigo{background:#e5e8ff;color:#4338ca}
.pa-pill.amber{background:#fef3c7;color:#b45309}.pa-pill.pink{background:#fce7f3;color:#be185d}.pa-pill.gray{background:#eceef5;color:#4b5271}
.pa-status{display:inline-flex;align-items:center;gap:5px;padding:3px 9px;border-radius:5px;font-size:.66rem;font-weight:700}
.pa-status::before{content:"";width:6px;height:6px;border-radius:50%;background:currentColor}
.pa-status.on{background:#dcfce7;color:#15803d}.pa-status.off{background:#ffe4e6;color:#be123c}
.pa-kebab.btn{width:28px;height:28px;padding:0;display:inline-flex;align-items:center;justify-content:center;background:#f4f5fb;border:0;color:#4b5271;border-radius:7px}
.pa-kebab.btn::after{display:none}
.pa-kebab.btn:hover,.pa-kebab.btn:focus,.pa-kebab.show{background:#e8eaf7!important;color:#1f2340!important}
.pa-menu .dropdown-item{font-size:.78rem;padding:.3rem .9rem;display:flex;align-items:center;gap:8px}
.pa-empty{padding:40px 0;text-align:center;color:#8a90a6;font-size:.8rem}
.pa-empty svg{font-size:1.6rem;margin-bottom:6px;color:#b3b8cc}
.pa-foot{display:flex;flex-wrap:wrap;gap:10px;align-items:center;justify-content:space-between;padding:10px 14px;border-top:1px solid #edf0f7;font-size:.76rem;color:#4b5271}
.pa-pager{display:flex;align-items:center;gap:4px}
.pa-pager button{min-width:28px;height:28px;border-radius:6px;border:1px solid #e3e6f0;background:#fff;color:#3b4160;font-size:.75rem;display:inline-flex;align-items:center;justify-content:center;padding:0 6px}
.pa-pager button.active{background:#4f46e5;border-color:#4f46e5;color:#fff;font-weight:700}
.pa-pager button:disabled{opacity:.45;cursor:not-allowed}
.pa-rows{border:1px solid #e3e6f0;border-radius:6px;font-size:.75rem;height:28px;padding:0 6px;background:#fff}

/* modals */
.pa-modal .modal-content{border:0;border-radius:12px;overflow:hidden}
.pa-modal .modal-header{padding:.75rem 1.1rem;border-bottom:1px solid #edf0f7}
.pa-modal .modal-title{font-size:.95rem;font-weight:700;color:#1f2340}
.pa-modal .modal-body{padding:1rem 1.1rem;max-height:70vh;overflow-y:auto}
.pa-modal .modal-footer{padding:.6rem 1.1rem;border-top:1px solid #edf0f7;background:#fafbff}
.pa-sec{margin-bottom:16px}
.pa-sec-title{display:flex;align-items:center;gap:8px;font-size:.68rem;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#7a809c;margin-bottom:8px}
.pa-sec-title::after{content:"";flex:1;height:1px;background:#edf0f7}
.pa-label{font-size:.74rem;font-weight:600;color:#3b4160;margin-bottom:3px;display:block}
.pa-help{font-size:.7rem;color:#8a90a6;margin-top:3px}
.pa-err{font-size:.7rem;color:#dc2626;margin-top:3px}
.pa-seg{display:inline-flex;padding:3px;border-radius:8px;background:#f1f3f9;gap:2px}
.pa-seg button{border:0;background:transparent;padding:5px 14px;border-radius:6px;font-size:.76rem;font-weight:600;color:#6b7190}
.pa-seg button.active{background:#fff;color:#4f46e5;box-shadow:0 1px 3px rgba(20,24,60,.12)}
.pa-chips{display:flex;flex-wrap:wrap;gap:6px}
.pa-chip{border:1px solid #dfe3ee;background:#fff;border-radius:20px;padding:4px 13px;font-size:.75rem;font-weight:600;color:#4b5271}
.pa-chip:hover{border-color:#a5a6fb}
.pa-chip.active{background:#eef0ff;border-color:#6366f1;color:#4338ca}
.pa-drop{border:1.5px dashed #c7cbe0;border-radius:10px;padding:18px 12px;text-align:center;background:#fafbff;cursor:pointer;transition:.15s}
.pa-drop:hover,.pa-drop.drag{border-color:#6366f1;background:#f2f3ff}
.pa-drop.err{border-color:#f87171;background:#fff7f7}
.pa-drop svg{font-size:1.5rem;color:#6366f1}
.pa-drop b{display:block;font-size:.8rem;color:#1f2340;margin-top:4px}
.pa-drop span{font-size:.7rem;color:#8a90a6}
.pa-media{display:flex;gap:12px;align-items:center;padding:10px;border:1px solid #edf0f7;border-radius:10px;background:#fafbff}
.pa-media-thumb{width:84px;height:84px;border-radius:8px;object-fit:cover;background:#eceef5;flex-shrink:0}
.pa-media-name{font-size:.78rem;font-weight:600;color:#1f2340;word-break:break-all}
.pa-media-meta{font-size:.7rem;color:#8a90a6;margin-bottom:6px}
.pa-switch .form-check-label{font-size:.78rem;color:#3b4160}
.pa-view-media{display:flex;justify-content:center;background:#f4f5fb;border-radius:10px;padding:8px;margin-bottom:14px}
.pa-view-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:12px 18px}
@media(max-width:575px){.pa-view-grid{grid-template-columns:1fr}}
.pa-view-grid dt{font-size:.68rem;font-weight:600;color:#8a90a6;text-transform:uppercase;letter-spacing:.04em;margin-bottom:2px}
.pa-view-grid dd{margin:0;font-size:.82rem;color:#1f2340;font-weight:500}
.pa-view-grid .full{grid-column:1/-1}
.pa-tags{display:flex;flex-wrap:wrap;gap:5px}
.pa-tag{background:#eef0ff;color:#4338ca;font-size:.7rem;padding:2px 8px;border-radius:12px;font-weight:600}
`;

/* ------------------------------------------------------------------ */
/* Media picker (drop zone + preview)                                  */
/* ------------------------------------------------------------------ */
const MediaPicker = ({ file, onFile, existingUrl, error }) => {
    const inputRef = useRef(null);
    const [drag, setDrag] = useState(false);

    const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
    useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

    const pick = (f) => {
        if (!f) return;
        const ext = f.name.split('.').pop().toLowerCase();
        if (!ALLOWED_EXT.includes(ext)) {
            notifyError(INVALID_FORMAT_MSG);
            return;
        }
        onFile(f);
    };

    const url = previewUrl || existingUrl;
    const isVideo = file ? file.type.startsWith('video') : VIDEO_RE.test(existingUrl || '');

    return (
        <>
            <input
                ref={inputRef}
                type="file"
                hidden
                accept="image/*,video/*"
                onChange={(e) => { pick(e.target.files[0]); e.target.value = ''; }}
            />
            {url ? (
                <div className="pa-media">
                    {isVideo
                        ? <video className="pa-media-thumb" src={url} muted />
                        : <img className="pa-media-thumb" src={url} alt="preview" />}
                    <div style={{ minWidth: 0 }}>
                        <div className="pa-media-name">{file ? file.name : 'Current file'}</div>
                        <div className="pa-media-meta">
                            {file ? `${formatSize(file.size)} · new file selected` : 'Currently used by this advertisement'}
                        </div>
                        <div className="d-flex gap-2">
                            <Button type="button" size="sm" className="pa-btn pa-btn-ghost" onClick={() => inputRef.current?.click()}>
                                Replace
                            </Button>
                            {file && (
                                <Button type="button" size="sm" className="pa-btn pa-btn-ghost" onClick={() => onFile(null)}>
                                    Remove
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div
                    className={`pa-drop ${drag ? 'drag' : ''} ${error ? 'err' : ''}`}
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                    onDragLeave={() => setDrag(false)}
                    onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files[0]); }}
                >
                    <CloudArrowUp />
                    <b>Click to upload or drag &amp; drop</b>
                    <span>Images (png, jpg, jpeg, webp, gif) or videos (mp4, mov, avi, mkv, webm)</span>
                </div>
            )}
            {error && <div className="pa-err">{error}</div>}
        </>
    );
};

/* ------------------------------------------------------------------ */
/* Add / Edit form modal (shared)                                      */
/* ------------------------------------------------------------------ */
const animatedComponents = makeAnimated();

const AdFormModal = ({ mode, initial, existingUrl, productOptions, outletOptions, submitting, onClose, onSubmit }) => {
    const isEdit = mode === 'edit';
    const [values, setValues] = useState(initial.values);
    const [product, setProduct] = useState(initial.product);
    const [outlets, setOutlets] = useState(initial.outlets);
    const [file, setFile] = useState(null);
    const [errors, setErrors] = useState({});

    const isSplash = values.isSplashScreenAd === 'true';

    const set = (name, val) => {
        setValues((v) => ({ ...v, [name]: val }));
        setErrors((e) => ({ ...e, [name]: undefined }));
    };

    const validate = () => {
        const errs = {};
        if (!values.title.trim()) errs.title = 'Please enter a title.';
        if (!isSplash && !values.redirectTo) errs.redirectTo = 'Choose where this pop-up should take the user.';
        if (!isSplash && values.redirectTo === 'PRODUCT' && !product) errs.product = 'Select a product for this pop-up.';
        if (!isEdit && !file) errs.file = 'Please upload an image or video.';
        return errs;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const errs = validate();
        setErrors(errs);
        if (Object.keys(errs).length) return;
        onSubmit({ values, product, outlets, file });
    };

    return (
        <Modal show onHide={submitting ? undefined : onClose} size="lg" centered backdrop="static" dialogClassName="pa-modal">
            <Modal.Header closeButton={!submitting}>
                <Modal.Title>{isEdit ? 'Update Pop-up Advertisement' : 'Add Pop-up Advertisement'}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit} noValidate>
                <Modal.Body>
                    {/* Basics */}
                    <div className="pa-sec">
                        <div className="pa-sec-title">Basics</div>
                        <div className="row g-2">
                            <div className={isEdit ? 'col-md-8' : 'col-12'}>
                                <label className="pa-label" htmlFor="pa-title">Title <span className="text-danger">*</span></label>
                                <Form.Control
                                    id="pa-title"
                                    className="pa-input"
                                    placeholder="e.g. Weekend Coffee Offer"
                                    value={values.title}
                                    isInvalid={!!errors.title}
                                    onChange={(e) => set('title', e.target.value)}
                                    autoFocus
                                />
                                {errors.title && <div className="pa-err">{errors.title}</div>}
                            </div>
                            {isEdit && (
                                <div className="col-md-4">
                                    <label className="pa-label">Status</label>
                                    <div className="pa-seg w-100">
                                        <button type="button" className={values.isActive === 'true' ? 'active' : ''} style={{ flex: 1 }} onClick={() => set('isActive', 'true')}>Active</button>
                                        <button type="button" className={values.isActive === 'false' ? 'active' : ''} style={{ flex: 1 }} onClick={() => set('isActive', 'false')}>Inactive</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Placement */}
                    <div className="pa-sec">
                        <div className="pa-sec-title">Where to show</div>
                        <div className="pa-seg">
                            <button type="button" className={!isSplash ? 'active' : ''} onClick={() => set('isSplashScreenAd', 'false')}>In-app screen</button>
                            <button type="button" className={isSplash ? 'active' : ''} onClick={() => set('isSplashScreenAd', 'true')}>Splash screen</button>
                        </div>
                        <div className="pa-help">
                            {isSplash
                                ? 'Shown when the app launches. No redirect needed.'
                                : 'Shown as a pop-up inside the app and takes the user to a screen when tapped.'}
                        </div>

                        {!isSplash && (
                            <div className="mt-3">
                                <label className="pa-label">Take user to <span className="text-danger">*</span></label>
                                <div className="pa-chips">
                                    {REDIRECT_OPTIONS.map((o) => (
                                        <button
                                            key={o.value}
                                            type="button"
                                            className={`pa-chip ${values.redirectTo === o.value ? 'active' : ''}`}
                                            onClick={() => set('redirectTo', o.value)}
                                        >
                                            {o.label}
                                        </button>
                                    ))}
                                </div>
                                {errors.redirectTo && <div className="pa-err">{errors.redirectTo}</div>}

                                {values.redirectTo === 'PRODUCT' && (
                                    <div className="mt-3">
                                        <label className="pa-label">Product <span className="text-danger">*</span></label>
                                        <Select
                                            options={productOptions}
                                            value={product}
                                            onChange={(v) => { setProduct(v); setErrors((e) => ({ ...e, product: undefined })); }}
                                            isClearable
                                            placeholder="Search and select a product"
                                            styles={selectStyles}
                                            menuPortalTarget={document.body}
                                        />
                                        {errors.product && <div className="pa-err">{errors.product}</div>}
                                    </div>
                                )}

                                {values.redirectTo === 'SUBSCRIPTION' && (
                                    <Form.Check
                                        type="switch"
                                        id="pa-sub-switch"
                                        className="pa-switch mt-3"
                                        label="Mark as a subscription ad"
                                        checked={values.isSubscriptionAd === 'true'}
                                        onChange={(e) => set('isSubscriptionAd', e.target.checked ? 'true' : 'false')}
                                    />
                                )}
                            </div>
                        )}
                    </div>

                    {/* Audience */}
                    {!isSplash && (
                        <div className="pa-sec">
                            <div className="pa-sec-title">Audience</div>
                            <label className="pa-label">Excluded outlets <span className="text-muted fw-normal">(optional)</span></label>
                            <Select
                                isMulti
                                components={animatedComponents}
                                value={outlets}
                                onChange={(v) => setOutlets(v || [])}
                                options={outletOptions}
                                styles={selectStyles}
                                placeholder="Pop-up is shown at all outlets unless you pick some"
                                closeMenuOnSelect={false}
                                menuPortalTarget={document.body}
                            />
                        </div>
                    )}

                    {/* Media */}
                    <div className="pa-sec mb-0">
                        <div className="pa-sec-title">Media</div>
                        <label className="pa-label">
                            {isEdit ? 'Image / video' : <>Image / video <span className="text-danger">*</span></>}
                            {isEdit && <span className="text-muted fw-normal"> — replace only if needed</span>}
                        </label>
                        <MediaPicker file={file} onFile={(f) => { setFile(f); setErrors((e) => ({ ...e, file: undefined })); }} existingUrl={isEdit ? existingUrl : null} error={errors.file} />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button type="button" className="pa-btn pa-btn-ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
                    <Button type="submit" className="pa-btn pa-btn-primary" disabled={submitting}>
                        {submitting ? <><Spinner size="sm" /> {isEdit ? 'Updating…' : 'Adding…'}</> : isEdit ? 'Update' : 'Add Advertisement'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

/* ------------------------------------------------------------------ */
/* View modal                                                          */
/* ------------------------------------------------------------------ */
const ViewModal = ({ row, canUpdate, onClose, onEdit }) => {
    const label = getScreenLabel(row);
    const isVideo = VIDEO_RE.test(row?.advertisementImage || '');
    return (
        <Modal show onHide={onClose} centered size="lg" dialogClassName="pa-modal">
            <Modal.Header closeButton>
                <Modal.Title>Advertisement Details</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="pa-view-media">
                    {isVideo ? (
                        <video src={row.advertisementImage} controls style={{ maxHeight: '36vh', maxWidth: '100%', borderRadius: 8 }} />
                    ) : (
                        <img src={row.advertisementImage || '/dummy-image.png'} alt={row.title} style={{ maxHeight: '36vh', maxWidth: '100%', borderRadius: 8 }} />
                    )}
                </div>
                <dl className="pa-view-grid mb-0">
                    <div className="full"><dt>Title</dt><dd>{row.title}</dd></div>
                    <div><dt>Status</dt><dd><span className={`pa-status ${row.isActive ? 'on' : 'off'}`}>{row.isActive ? 'ACTIVE' : 'INACTIVE'}</span></dd></div>
                    <div><dt>Screen</dt><dd><span className={`pa-pill ${screenClass(label)}`}>{label}</span></dd></div>
                    <div><dt>Splash screen ad</dt><dd>{row.isSplashScreenAd ? 'Yes' : 'No'}</dd></div>
                    <div><dt>Subscription ad</dt><dd>{row.isSubscriptionAd ? 'Yes' : 'No'}</dd></div>
                    <div><dt>Product</dt><dd>{row.productId?.name || 'No Product Allocated'}</dd></div>
                    <div><dt>Created</dt><dd>{formatDate(row.createdAt)}</dd></div>
                    <div className="full">
                        <dt>Excluded outlets</dt>
                        <dd>
                            {row.excludedOutlets?.length > 0
                                ? <div className="pa-tags">{row.excludedOutlets.map((o) => <span className="pa-tag" key={o._id}>{o.name}</span>)}</div>
                                : 'No outlets excluded'}
                        </dd>
                    </div>
                </dl>
            </Modal.Body>
            <Modal.Footer>
                <Button className="pa-btn pa-btn-ghost" onClick={onClose}>Close</Button>
                {canUpdate && (
                    <Button className="pa-btn pa-btn-primary" onClick={() => onEdit(row)}>
                        <Pencil size={12} /> Edit
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */
const PopupAdvertisement = () => {
    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin] = useState(currentSkin);
    const { access, loginEmail } = useSelector((state) => state.login);
    const email = loginEmail || localStorage.getItem('email');
    const accessToken = localStorage.getItem('authToken');

    const [addAccess, setAddAccess] = useState([]);
    useEffect(() => {
        if (Array.isArray(access)) {
            setAddAccess(access.filter((p) => p?.startsWith('PopupAdvertisement:')));
        }
    }, [access]);

    const has = (perm) => addAccess?.includes(`PopupAdvertisement:${perm}`) || email === ADMIN_EMAIL;
    const canAdd = has('add');
    const canGet = has('get');
    const canUpdate = has('update');
    const canDelete = has('delete');

    // data
    const [popUpData, setPopUpData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [outlets, setOutlets] = useState([]);
    const [productsList, setProductsList] = useState([]);
    const [loading, setLoading] = useState(false);

    // toolbar
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [splashScreenFilter, setSplashScreenFilter] = useState('');
    const [appliedFilters, setAppliedFilters] = useState({});

    // table
    const [sort, setSort] = useState({ key: null, dir: 'asc' });
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // modals
    const [formModal, setFormModal] = useState(null); // { mode: 'add' | 'edit', row? }
    const [viewRow, setViewRow] = useState(null);
    const [deleteRow, setDeleteRow] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const authHeaders = { Authorization: `Bearer ${accessToken}` };
    const multipartHeaders = { ...authHeaders, 'Content-Type': 'multipart/form-data' };

    /* ---------------- API ---------------- */
    const getInAppAddvertisement = async (filters = {}) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.isActive !== undefined && filters.isActive !== '') {
                params.append('isActive', filters.isActive);
            }
            if (filters.isSplashScreenAd !== undefined && filters.isSplashScreenAd !== '') {
                params.append('isSplashScreenAd', filters.isSplashScreenAd);
            }
            const url = `in-app-advertisement${params.toString() ? `?${params.toString()}` : ''}`;
            const response = await axiosInstance.get(url, { headers: authHeaders });

            const list = response?.data?.data?.inAppAdvertisement || [];
            list.forEach((item, index) => { item.serialNumber = index + 1; });
            const newList = [...list].reverse();

            setPopUpData(newList);
            setFilteredData(newList);
        } catch (error) {
            if (error.response?.data?.status === false) notifyError(asText(error.response.data.error));
        } finally {
            setLoading(false);
        }
    };

    const getOutlets = async () => {
        try {
            const response = await axiosInstance.get('outlet', { headers: authHeaders });
            if (response?.data?.status === true) {
                setOutlets(response.data.data.outlets?.filter((o) => o?.status === 'ACTIVE') || []);
            }
        } catch (error) {
            if (error.response?.data?.status === false) notifyError(asText(error.response.data.error));
        }
    };

    const getProductsList = async () => {
        try {
            const response = await axiosInstance.get('product', { headers: authHeaders });
            if (response?.data?.status === true) setProductsList(response.data.data.products || []);
        } catch (error) {
            if (error.response?.data?.status === false) notifyError(asText(error.response.data.error));
        }
    };

    useEffect(() => {
        getInAppAddvertisement();
        getOutlets();
        getProductsList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ---------------- search / filters ---------------- */
    const applySearch = (text, source) => {
        const q = text.trim().toLowerCase();
        if (!q) return source;
        return source.filter((item) =>
            item?.title?.toLowerCase()?.includes(q) || item?.productId?.name?.toLowerCase()?.includes(q));
    };

    useEffect(() => {
        setFilteredData(applySearch(searchText, popUpData));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText, popUpData]);

    useEffect(() => { setPage(1); }, [searchText]);

    const handleApplyFilters = () => {
        const filters = {};
        if (statusFilter) filters.isActive = statusFilter;
        if (splashScreenFilter !== '') filters.isSplashScreenAd = splashScreenFilter;
        setAppliedFilters(filters);
        setPage(1);
        getInAppAddvertisement(filters);
    };

    const handleClearFilters = () => {
        setStatusFilter('');
        setSplashScreenFilter('');
        setSearchText('');
        setAppliedFilters({});
        setPage(1);
        getInAppAddvertisement({});
    };

    /* ---------------- stats ---------------- */
    const stats = useMemo(() => ({
        total: popUpData.length,
        active: popUpData.filter((a) => a.isActive === true).length,
        inactive: popUpData.filter((a) => a.isActive !== true).length,
        excluded: popUpData.filter((a) => a.excludedOutlets?.length > 0).length,
    }), [popUpData]);

    /* ---------------- sorting & paging ---------------- */
    const sortValue = (row, key) => {
        switch (key) {
            case 'title': return (row.title || '').toLowerCase();
            case 'screen': return getScreenLabel(row);
            case 'product': return (row.productId?.name || '').toLowerCase();
            case 'excluded': return row.excludedOutlets?.length || 0;
            case 'status': return row.isActive ? 1 : 0;
            default: return 0;
        }
    };

    const sortedData = useMemo(() => {
        if (!sort.key) return filteredData;
        const dir = sort.dir === 'asc' ? 1 : -1;
        return [...filteredData].sort((a, b) => {
            const av = sortValue(a, sort.key);
            const bv = sortValue(b, sort.key);
            return av > bv ? dir : av < bv ? -dir : 0;
        });
    }, [filteredData, sort]);

    const handleSort = (key) => {
        setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
        setPage(1);
    };

    const totalPages = Math.max(1, Math.ceil(sortedData.length / rowsPerPage));
    useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

    const startIdx = (page - 1) * rowsPerPage;
    const pageData = sortedData.slice(startIdx, startIdx + rowsPerPage);
    const showingFrom = sortedData.length === 0 ? 0 : startIdx + 1;
    const showingTo = Math.min(startIdx + rowsPerPage, sortedData.length);

    const pageNumbers = useMemo(() => {
        const maxButtons = 5;
        let start = Math.max(1, page - 2);
        let end = Math.min(totalPages, start + maxButtons - 1);
        start = Math.max(1, end - maxButtons + 1);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    }, [page, totalPages]);

    /* ---------------- add / edit ---------------- */
    const productOptions = useMemo(() => productsList.map((p) => ({ value: p._id, label: p.name })), [productsList]);
    const outletOptions = useMemo(() => outlets.map((o) => ({ value: o._id, label: `${o.name} (${o.city})` })), [outlets]);

    const openAdd = () => setFormModal({ mode: 'add' });
    const openEdit = (row) => { setViewRow(null); setFormModal({ mode: 'edit', row }); };

    const getInitial = () => {
        if (formModal?.mode === 'edit') {
            const r = formModal.row;
            return {
                values: {
                    title: r.title || '',
                    isActive: String(r.isActive ?? true),
                    isSplashScreenAd: String(r.isSplashScreenAd ?? false),
                    isSubscriptionAd: String(r.isSubscriptionAd ?? false),
                    redirectTo: r.redirectTo || '',
                },
                product: r.productId ? { value: r.productId._id, label: r.productId.name } : null,
                outlets: (r.excludedOutlets || []).map((o) => ({ value: o._id, label: o.name })),
            };
        }
        return {
            values: { title: '', isActive: 'true', isSplashScreenAd: 'false', isSubscriptionAd: 'false', redirectTo: '' },
            product: null,
            outlets: [],
        };
    };

    const handleAddSubmit = async ({ values, product, outlets: selOutlets, file }) => {
        setIsSaving(true);
        try {
            const splash = values.isSplashScreenAd === 'true';
            const fd = new FormData();
            fd.append('title', values.title.trim());
            fd.append('isSplashScreenAd', String(splash));
            fd.append('isSubscriptionAd', !splash && values.redirectTo === 'SUBSCRIPTION' && values.isSubscriptionAd === 'true' ? 'true' : 'false');
            if (!splash) {
                if (values.redirectTo) fd.append('redirectTo', values.redirectTo);
                if (values.redirectTo === 'PRODUCT' && product?.value) fd.append('productId', product.value);
                selOutlets.forEach((o, i) => fd.append(`excludedOutlets[${i}]`, o.value));
            }
            fd.append('advertisementImage', file);

            const response = await axiosInstance.post('in-app-advertisement', fd, { headers: multipartHeaders });
            if (response?.data?.status === true) {
                notifySuccess(response?.data?.data?.message);
                setFormModal(null);
                getInAppAddvertisement(appliedFilters);
            }
        } catch (error) {
            notifyError(getErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    const handleEditSubmit = async ({ values, product, outlets: selOutlets, file }) => {
        setIsSaving(true);
        const row = formModal.row;
        try {
            const splash = values.isSplashScreenAd === 'true';
            const fd = new FormData();
            fd.append('_id', row._id);
            fd.append('title', values.title.trim());
            fd.append('isActive', values.isActive);
            fd.append('isSplashScreenAd', String(splash));
            fd.append('isSubscriptionAd', !splash && values.redirectTo === 'SUBSCRIPTION' && values.isSubscriptionAd === 'true' ? 'true' : 'false');
            if (!splash && values.redirectTo) fd.append('redirectTo', values.redirectTo);
            fd.append('productId', !splash && values.redirectTo === 'PRODUCT' && product?.value ? product.value : 'null');
            if (!splash && selOutlets.length > 0) {
                selOutlets.forEach((o, i) => fd.append(`excludedOutlets[${i}]`, o.value));
            } else {
                fd.append('excludedOutlets', 'null');
            }

            const response = await axiosInstance.put('in-app-advertisement', fd, { headers: multipartHeaders });
            if (response?.data?.status !== true) return;

            // new media is uploaded as a second request, same as the previous "Change File" flow
            if (file) {
                const fileFd = new FormData();
                fileFd.append('advertisementImage', file);
                fileFd.append('_id', row._id);
                try {
                    await axiosInstance.put('in-app-advertisement', fileFd, { headers: multipartHeaders });
                } catch (fileErr) {
                    notifyError(`Details saved, but the file upload failed: ${getErrorMessage(fileErr) || 'please try again'}`);
                    setFormModal(null);
                    getInAppAddvertisement(appliedFilters);
                    return;
                }
            }

            notifySuccess(response?.data?.data?.message);
            setFormModal(null);
            getInAppAddvertisement(appliedFilters);
        } catch (error) {
            notifyError(getErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    /* ---------------- delete ---------------- */
    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            const response = await axiosInstance.delete(`in-app-advertisement?_id=${deleteRow._id}`, { headers: authHeaders });
            if (response?.data?.status === true) {
                notifySuccess(response?.data?.data?.message);
                setDeleteRow(null);
                getInAppAddvertisement(appliedFilters);
            }
        } catch (error) {
            const res = error.response;
            notifyError(
                (res?.status === 403 && asText(res?.data?.message)) || asText(res?.data?.error) || getErrorMessage(error)
            );
        } finally {
            setIsDeleting(false);
        }
    };

    /* ---------------- render ---------------- */
    const SortTh = ({ k, children }) => (
        <th className={`sortable ${sort.key === k ? 'active' : ''}`} onClick={() => handleSort(k)}>
            {children}<ChevronExpand size={11} className="pa-sort" />
        </th>
    );

    return (
        <React.Fragment>
            <style>{css}</style>
            <Header onSkin={setSkin} />
            <div className="main main-app p-2 p-lg-3">
                <div className="pa-wrap">

                    {/* Hero */}
                    <div className="pa-hero">
                        <div className="pa-hero-left">
                            <div className="pa-hero-icon"><Megaphone /></div>
                            <div>
                                <h1>Popup Advertisement Management</h1>
                                <p>Create, manage and track your popup advertisements with ease.</p>
                            </div>
                        </div>
                        <div className="pa-hero-art d-none d-md-block">
                            <div className="pa-hero-tile"><ImageIcon /></div>
                            {/* <span className="pa-hero-new">New</span> */}
                        </div>
                    </div>

                    {/* Toolbar */}
                    <div className="pa-card pa-toolbar">
                        <div className="pa-field">
                            <label>Search</label>
                            <div className="pa-search">
                                <Form.Control
                                    className="pa-input"
                                    placeholder="Search by title or product..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && setPage(1)}
                                />
                                <Button className="pa-btn pa-btn-primary" aria-label="Search" onClick={() => setPage(1)}>
                                    <Search size={13} />
                                </Button>
                            </div>
                        </div>
                        <div className="pa-field">
                            <label>Status Filter</label>
                            <Form.Select className="pa-input" style={{ minWidth: 150 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                <option value="">All Status</option>
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </Form.Select>
                        </div>
                        <div className="pa-field">
                            <label>Splash Screen</label>
                            <Form.Select className="pa-input" style={{ minWidth: 150 }} value={splashScreenFilter} onChange={(e) => setSplashScreenFilter(e.target.value)}>
                                <option value="">All</option>
                                <option value="true">Yes</option>
                                <option value="false">No</option>
                            </Form.Select>
                        </div>
                        <Button className="pa-btn pa-btn-primary" onClick={handleApplyFilters}>
                            <Filter size={12} /> Apply Filters
                        </Button>
                        <Button className="pa-btn pa-btn-ghost" onClick={handleClearFilters}>
                            <ArrowClockwise size={12} /> Clear
                        </Button>
                        <div className="pa-spacer" />
                        {canAdd && (
                            <Button className="pa-btn pa-btn-primary" onClick={openAdd}>
                                <Plus size={16} /> Add New
                            </Button>
                        )}
                    </div>

                    {/* Stats */}
                    <div className="pa-stats">
                        <div className="pa-stat indigo"><div className="pa-stat-icon"><Megaphone /></div><div><b>{stats.total}</b><span>Total Advertisements</span></div></div>
                        <div className="pa-stat green"><div className="pa-stat-icon"><Eye /></div><div><b>{stats.active}</b><span>Active</span></div></div>
                        <div className="pa-stat amber"><div className="pa-stat-icon"><ExclamationCircle /></div><div><b>{stats.inactive}</b><span>Inactive</span></div></div>
                        <div className="pa-stat rose"><div className="pa-stat-icon"><EyeSlash /></div><div><b>{stats.excluded}</b><span>Excluded Outlets</span></div></div>
                    </div>

                    {/* Table */}
                    {canGet && (
                        <div className="pa-card">
                            <div className="pa-table-wrap">
                                <table className="pa-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 70 }}>Sr.No</th>
                                            <SortTh k="title">Title</SortTh>
                                            <SortTh k="screen">Screen</SortTh>
                                            <SortTh k="product">Product Name</SortTh>
                                            <SortTh k="excluded">Excluded Outlets</SortTh>
                                            <SortTh k="status">Status</SortTh>
                                            <th style={{ width: 80 }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr><td colSpan={7}><div className="pa-empty"><Spinner size="sm" /> <span className="ms-2">Loading…</span></div></td></tr>
                                        ) : pageData.length === 0 ? (
                                            <tr><td colSpan={7}><div className="pa-empty"><Megaphone /><div>No advertisements found</div></div></td></tr>
                                        ) : pageData.map((row) => {
                                            const label = getScreenLabel(row);
                                            const video = VIDEO_RE.test(row?.advertisementImage || '');
                                            return (
                                                <tr key={row._id || row.serialNumber}>
                                                    <td><span className="pa-sr">{row.serialNumber}</span></td>
                                                    <td>
                                                        <div className="pa-title-cell">
                                                            {video ? (
                                                                <video className="pa-avatar" src={row.advertisementImage} muted onClick={() => setViewRow(row)} />
                                                            ) : (
                                                                <img className="pa-avatar" src={row.advertisementImage || '/dummy-image.png'} alt={row.title} onClick={() => setViewRow(row)} />
                                                            )}
                                                            <div>
                                                                <div className="pa-title">{row.title}</div>
                                                                <div className="pa-date">{formatDate(row.createdAt)}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td><span className={`pa-pill ${screenClass(label)}`}>{label}</span></td>
                                                    <td>{row.productId?.name || 'No Product Allocated'}</td>
                                                    <td>{excludedText(row)}</td>
                                                    <td><span className={`pa-status ${row.isActive === true ? 'on' : 'off'}`}>{row.isActive === true ? 'ACTIVE' : 'INACTIVE'}</span></td>
                                                    <td>
                                                        <Dropdown align="end">
                                                            <Dropdown.Toggle variant="light" className="pa-kebab" aria-label="Actions">
                                                                <ThreeDotsVertical size={14} />
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu renderOnMount className="pa-menu" popperConfig={{ strategy: 'fixed' }}>
                                                                {canGet && <Dropdown.Item className="text-success" onClick={() => setViewRow(row)}><Eye size={13} /> View</Dropdown.Item>}
                                                                {canUpdate && <Dropdown.Item className="text-primary" onClick={() => openEdit(row)}><Pencil size={13} /> Edit</Dropdown.Item>}
                                                                {canDelete && <Dropdown.Item className="text-danger" onClick={() => setDeleteRow(row)}><Trash size={13} /> Delete</Dropdown.Item>}
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="pa-foot">
                                <div>Showing {showingFrom} to {showingTo} of {sortedData.length} entries</div>
                                <div className="d-flex align-items-center gap-3">
                                    <label className="d-flex align-items-center gap-2 mb-0">
                                        Rows
                                        <select className="pa-rows" value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}>
                                            {[5, 10, 25, 50].map((n) => <option key={n} value={n}>{n}</option>)}
                                        </select>
                                    </label>
                                    <div className="pa-pager">
                                        <button disabled={page === 1} onClick={() => setPage(page - 1)} aria-label="Previous page"><ChevronLeft size={12} /></button>
                                        {pageNumbers.map((n) => (
                                            <button key={n} className={n === page ? 'active' : ''} onClick={() => setPage(n)}>{n}</button>
                                        ))}
                                        <button disabled={page === totalPages} onClick={() => setPage(page + 1)} aria-label="Next page"><ChevronRight size={12} /></button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Add / Edit */}
            {formModal && (
                <AdFormModal
                    key={formModal.row?._id || 'add'}
                    mode={formModal.mode}
                    initial={getInitial()}
                    existingUrl={formModal.row?.advertisementImage}
                    productOptions={productOptions}
                    outletOptions={outletOptions}
                    submitting={isSaving}
                    onClose={() => setFormModal(null)}
                    onSubmit={formModal.mode === 'edit' ? handleEditSubmit : handleAddSubmit}
                />
            )}

            {/* View */}
            {viewRow && <ViewModal row={viewRow} canUpdate={canUpdate} onClose={() => setViewRow(null)} onEdit={openEdit} />}

            {/* Delete */}
            {deleteRow && (
                <Modal show onHide={isDeleting ? undefined : () => setDeleteRow(null)} centered backdrop="static" dialogClassName="pa-modal">
                    <Modal.Header closeButton={!isDeleting}>
                        <Modal.Title>Delete advertisement?</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ fontSize: '0.82rem' }}>
                        <div className="d-flex gap-2">
                            <InfoCircle className="text-danger mt-1" />
                            <div>
                                You are about to delete <b>{deleteRow.title}</b>. This action cannot be undone.
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button className="pa-btn pa-btn-ghost" onClick={() => setDeleteRow(null)} disabled={isDeleting}>Cancel</Button>
                        <Button variant="danger" className="pa-btn" onClick={handleDelete} disabled={isDeleting}>
                            {isDeleting ? <Spinner size="sm" /> : 'Delete'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </React.Fragment>
    );
};

export default PopupAdvertisement;